import React, { useState } from 'react';
import PrintPreviewFunctions from './PrintPreviewFunctions';
import './CSSPrintPreview.css';

const PrintPreview = ({ sizeIndex, checkboxOptions, projectScanForSend, orientationIndex, sendModeIndex, secondaryModeIndex }) => {
    const [pages, setPages] = useState([]);
    const [totalPages, setTotalPages] = useState(1);

    // Calculate dimensions based on paper size and orientation
    const isUSLetter = sizeIndex === 0;
    const isLandscape = orientationIndex === 1;
    const previewWidth = 700; // Reduced from 1000 to fit better in 70vh container

    // Check if we should use spreadsheet format - activate when 4th secondary option is selected regardless of primary mode
    const isSpreadsheetFormat = secondaryModeIndex === 1; // Any primary mode -> 4th secondary option (spreadsheet format)

    // Real paper aspect ratios
    const aspectRatios = {
        usLetter: {
            portrait: 1056 / 816, // height / width (11" / 8.5")
            landscape: 816 / 1056  // height / width (8.5" / 11") - swapped
        },
        a4: {
            portrait: 1123 / 794, // height / width (297mm / 210mm)
            landscape: 794 / 1123  // height / width (210mm / 297mm) - swapped
        }
    };

    const currentAspectRatio = isUSLetter
        ? (isLandscape ? aspectRatios.usLetter.landscape : aspectRatios.usLetter.portrait)
        : (isLandscape ? aspectRatios.a4.landscape : aspectRatios.a4.portrait);

    const previewHeight = previewWidth * currentAspectRatio;

    // For landscape, we're scaling based on the longer dimension (11" for US Letter, 297mm for A4)
    const realPaperWidth = isLandscape
        ? (isUSLetter ? 1056 : 1123)  // Use the longer dimension as width in landscape
        : (isUSLetter ? 816 : 794);   // Use normal width in portrait
    const scaleFactor = previewWidth / realPaperWidth;

    // Standard margins (1 inch = 96px at 96 DPI, scaled down)
    const marginSize = 96 * scaleFactor;

    const previewStyle = {
        width: `${previewWidth}px`,
        height: `${previewHeight}px`,
        fontSize: `${12 * scaleFactor}px`,
        marginBottom: '20px', // Add space between pages
        backgroundColor: 'white',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        position: 'relative'
    };

    const contentStyle = {
        position: 'absolute',
        top: `${marginSize}px`,
        left: `${marginSize}px`,
        right: `${marginSize}px`,
        bottom: `${marginSize}px`,
        width: `${previewWidth - (marginSize * 2)}px`,
        height: `${previewHeight - (marginSize * 2)}px`,
        overflow: 'hidden'
    };

    const baseParagraphStyle = {
        textIndent: `${24 * scaleFactor}px`, // Standard paragraph indent
        marginBottom: `${12 * scaleFactor}px`,
        fontSize: `${12 * scaleFactor}px`
        // Removed fixed lineHeight - will be applied dynamically per paragraph
    };

    // Header and footer styles
    const headerFooterStyle = {
        fontSize: `${10 * scaleFactor}px`,
        position: 'absolute'
    };

    const headerStyle = {
        top: `${marginSize * 0.5}px`,
        left: `${marginSize}px`,
        right: `${marginSize}px`,
        display: 'flex',
        justifyContent: 'space-between'
    };

    const footerStyle = {
        bottom: `${marginSize * 0.5}px`,
        left: `${marginSize}px`,
        textAlign: 'center'
    };

    // Spreadsheet table styles
    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: `${12 * scaleFactor}px`,
        border: `${1 * scaleFactor}px solid #000`
    };

    const tableHeaderStyle = {
        backgroundColor: '#f0f0f0',
        fontWeight: 'bold',
        textAlign: 'center',
        padding: `${8 * scaleFactor}px`,
        border: `${1 * scaleFactor}px solid #000`,
        fontSize: `${12 * scaleFactor}px`
    };

    const tableCellStyle = {
        padding: `${8 * scaleFactor}px`,
        border: `${1 * scaleFactor}px solid #000`,
        verticalAlign: 'top',
        fontSize: `${12 * scaleFactor}px`
    };

    // Helper function to extract font family from element or its children
    const extractFontFamily = (element) => {
        // First check the element itself
        if (element.style.fontFamily) {
            return element.style.fontFamily;
        }

        // Check computed styles if available
        if (window.getComputedStyle) {
            const computed = window.getComputedStyle(element);
            if (computed.fontFamily && computed.fontFamily !== 'serif' && computed.fontFamily !== 'sans-serif') {
                return computed.fontFamily;
            }
        }

        // Check for inline font-family in nested elements
        const elementsWithFont = element.querySelectorAll('[style*="font-family"]');
        if (elementsWithFont.length > 0) {
            const fontFamily = elementsWithFont[0].style.fontFamily;
            if (fontFamily) return fontFamily;
        }

        // Check for span elements with font families (from processFontStyling)
        const spans = element.querySelectorAll('span[style*="font-family"]');
        if (spans.length > 0) {
            return spans[0].style.fontFamily;
        }

        // Check parent elements for inherited fonts
        let parent = element.parentElement;
        while (parent) {
            if (parent.style.fontFamily) {
                return parent.style.fontFamily;
            }
            parent = parent.parentElement;
        }

        return 'sans-serif'; // default fallback
    };

    // Helper function to extract all styling from an element comprehensively
    const extractCompleteStyles = (element) => {
        const styles = {};

        // First, get the font-family from the element or its parents to preserve it
        const fontFamily = extractFontFamily(element);
        if (fontFamily) {
            styles['font-family'] = fontFamily;
        }

        // Get all inline styles
        if (element.style.cssText) {
            const inlineStyles = element.style.cssText.split(';');
            inlineStyles.forEach(style => {
                if (style.trim()) {
                    const [property, value] = style.split(':').map(s => s.trim());
                    if (property && value) {
                        styles[property] = value;
                    }
                }
            });
        }

        // Get computed styles for important properties if inline styles are missing
        if (window.getComputedStyle) {
            const computed = window.getComputedStyle(element);
            const importantProps = ['font-weight', 'font-style', 'text-decoration', 'color'];

            importantProps.forEach(prop => {
                const camelCase = prop.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                if (!styles[prop] && computed[camelCase] && computed[camelCase] !== 'normal') {
                    styles[prop] = computed[camelCase];
                }
            });
        }

        // Check for semantic tag styling (but don't override font-family!)
        const tagName = element.tagName.toLowerCase();
        if (tagName === 'strong' || tagName === 'b') {
            styles['font-weight'] = 'bold';
        }
        if (tagName === 'em' || tagName === 'i') {
            styles['font-style'] = 'italic';
        }
        if (tagName === 'u') {
            styles['text-decoration'] = 'underline';
        }

        return styles;
    };

    // Function to preserve paragraph structure while removing other HTML
    const preserveParagraphs = (html) => {
        if (!html) return '';

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Get all paragraph elements
        const paragraphs = tempDiv.querySelectorAll('p');
        let result = '';

        if (paragraphs.length > 0) {
            paragraphs.forEach((p, index) => {
                const innerHTML = p.innerHTML || '';
                if (innerHTML.trim()) {
                    // Extract font-family from paragraph OR nested elements
                    const fontFamily = extractFontFamily(p);

                    // Preserve ALL styling from the paragraph, including inline styles
                    const existingStyles = p.style.cssText;

                    // Build comprehensive style string that includes font-family
                    const styleString = `margin: 0 0 ${6 * scaleFactor}px 0; font-size: ${12 * scaleFactor}px; font-family: ${fontFamily}; ${existingStyles}`;

                    result += `<p style="${styleString}">${innerHTML}</p>`;
                }
            });
        } else {
            // If no paragraphs found, preserve the entire HTML structure with font handling
            const innerHTML = tempDiv.innerHTML || '';
            if (innerHTML.trim()) {
                // Check for font styling in the entire content
                const fontFamily = extractFontFamily(tempDiv);
                result = `<div style="font-size: ${12 * scaleFactor}px; font-family: ${fontFamily};">${innerHTML}</div>`;
            }
        }

        return result;
    };

    // Function to split text content based on available height
    const splitTextContent = (htmlContent, maxHeight, styles) => {
        if (!htmlContent || htmlContent.trim() === '') return [];

        // Create a temporary element to measure content height
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.top = '-9999px';
        tempDiv.style.left = '-9999px';
        tempDiv.style.width = `${previewWidth - (marginSize * 2)}px`;
        tempDiv.style.visibility = 'hidden';
        Object.assign(tempDiv.style, styles);
        document.body.appendChild(tempDiv);

        // Check if the entire content fits
        tempDiv.innerHTML = htmlContent;
        if (tempDiv.offsetHeight <= maxHeight) {
            document.body.removeChild(tempDiv);
            return [htmlContent];
        }

        // Content doesn't fit, need to split
        const chunks = [];

        // For HTML content with styling, preserve the structure
        if (htmlContent.includes('<') && htmlContent.includes('>')) {
            // Create a parser to handle the HTML content
            const parser = document.createElement('div');
            parser.innerHTML = htmlContent;

            // Extract all text nodes while preserving their complete parent styling
            const textNodes = [];
            const walker = document.createTreeWalker(
                parser,
                NodeFilter.SHOW_TEXT,
                null,
                false
            );

            let node;
            while (node = walker.nextNode()) {
                if (node.textContent.trim()) {
                    // Get complete styling from all parent elements
                    let parentElement = node.parentElement;
                    let completeStyles = {};
                    let parentTag = 'span';

                    // Walk up the parent chain and collect all styles
                    while (parentElement && parentElement !== parser) {
                        const elementStyles = extractCompleteStyles(parentElement);

                        // Merge styles (child styles override parent styles)
                        completeStyles = { ...elementStyles, ...completeStyles };

                        // Keep track of the immediate parent tag
                        if (parentElement === node.parentElement) {
                            parentTag = parentElement.tagName.toLowerCase();
                        }

                        parentElement = parentElement.parentElement;
                    }

                    // Convert styles object back to CSS string
                    const styleString = Object.entries(completeStyles)
                        .map(([prop, value]) => `${prop}: ${value}`)
                        .join('; ');

                    textNodes.push({
                        text: node.textContent,
                        styles: styleString,
                        parentTag: parentTag
                    });
                }
            }

            // Split by words while preserving complete styling
            let currentChunk = '';

            textNodes.forEach(textNode => {
                const words = textNode.text.split(/\s+/).filter(word => word.length > 0);

                words.forEach(word => {
                    const styledWord = `<span style="${textNode.styles}">${word}</span>`;
                    const testContent = currentChunk + (currentChunk ? ' ' : '') + styledWord;

                    tempDiv.innerHTML = testContent;

                    if (tempDiv.offsetHeight > maxHeight && currentChunk !== '') {
                        chunks.push(currentChunk.trim());
                        currentChunk = styledWord;
                    } else {
                        currentChunk = testContent;
                    }
                });
            });

            if (currentChunk.trim() !== '') {
                chunks.push(currentChunk.trim());
            }
        } else {
            // Fallback for plain text
            const words = htmlContent.split(' ');
            let currentChunk = '';

            for (let i = 0; i < words.length; i++) {
                const testChunk = currentChunk + (currentChunk ? ' ' : '') + words[i];
                tempDiv.textContent = testChunk;

                if (tempDiv.offsetHeight > maxHeight && currentChunk !== '') {
                    chunks.push(currentChunk.trim());
                    currentChunk = words[i];
                } else {
                    currentChunk = testChunk;
                }
            }

            if (currentChunk.trim() !== '') {
                chunks.push(currentChunk.trim());
            }
        }

        document.body.removeChild(tempDiv);
        return chunks.filter(chunk => chunk.trim() !== '');
    };

    // Function to split table cell content based on available height
    const splitTableCellContent = (html, maxHeight) => {
        if (!html || html.trim() === '') return [];

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Create temp element for measuring
        const tempMeasure = document.createElement('div');
        tempMeasure.style.position = 'absolute';
        tempMeasure.style.top = '-9999px';
        tempMeasure.style.left = '-9999px';
        tempMeasure.style.width = `${previewWidth - (marginSize * 2) - (16 * scaleFactor)}px`;
        tempMeasure.style.visibility = 'hidden';
        tempMeasure.style.fontSize = `${12 * scaleFactor}px`;
        tempMeasure.style.lineHeight = '1.5';
        tempMeasure.style.padding = `${8 * scaleFactor}px`;
        document.body.appendChild(tempMeasure);

        // Check if entire content fits
        tempMeasure.innerHTML = html;
        if (tempMeasure.offsetHeight <= maxHeight) {
            document.body.removeChild(tempMeasure);
            return [html];
        }

        // Content doesn't fit, need to split
        const paragraphs = Array.from(tempDiv.querySelectorAll('p'));
        if (paragraphs.length === 0) {
            // No paragraphs, treat as single content block
            const chunks = splitTextContent(html, maxHeight, {
                fontSize: `${12 * scaleFactor}px`,
                padding: `${8 * scaleFactor}px`,
                lineHeight: '1.5'
            });
            document.body.removeChild(tempMeasure);
            return chunks;
        }

        // Handle multiple paragraphs while preserving complete styling
        const splitContent = [];
        let currentChunk = '';

        for (const p of paragraphs) {
            const pHTML = p.outerHTML; // Use outerHTML to preserve styling
            const testContent = currentChunk + pHTML;

            tempMeasure.innerHTML = testContent;

            if (tempMeasure.offsetHeight > maxHeight && currentChunk !== '') {
                // Current chunk fits, but adding this paragraph would exceed height
                if (currentChunk.trim() !== '') {
                    splitContent.push(currentChunk.trim());
                }

                // Check if this single paragraph is too large and needs splitting
                tempMeasure.innerHTML = pHTML;
                if (tempMeasure.offsetHeight > maxHeight) {
                    // Split this paragraph while preserving ALL its styling
                    const completeStyles = extractCompleteStyles(p);
                    const pInnerHTML = p.innerHTML;
                    const fontFamily = extractFontFamily(p);

                    // Create comprehensive style string
                    const styleEntries = Object.entries(completeStyles)
                        .map(([prop, value]) => `${prop}: ${value}`)
                        .join('; ');

                    const baseStyle = `margin: 0 0 ${6 * scaleFactor}px 0; font-size: ${12 * scaleFactor}px; font-family: ${fontFamily};`;
                    const fullStyle = `${baseStyle} ${styleEntries}`;

                    const textChunks = splitTextContent(pInnerHTML, maxHeight, {
                        fontSize: `${12 * scaleFactor}px`,
                        padding: `${8 * scaleFactor}px`,
                        lineHeight: '1.5',
                        margin: `0 0 ${6 * scaleFactor}px 0`,
                        fontFamily: fontFamily,
                        ...completeStyles
                    });

                    textChunks.forEach((chunk) => {
                        // Preserve ALL styling in split chunks
                        splitContent.push(`<p style="${fullStyle}">${chunk}</p>`);
                    });
                    currentChunk = '';
                } else {
                    currentChunk = pHTML;
                }
            } else {
                currentChunk = testContent;
            }
        }

        // Add remaining content
        if (currentChunk.trim() !== '') {
            splitContent.push(currentChunk.trim());
        }

        document.body.removeChild(tempMeasure);
        return splitContent.filter(content => content.trim() !== '');
    };

    // Effect to handle pagination with continuous content flow and text breaking
    React.useEffect(() => {
        const calculatePages = () => {
            // Calculate available space per page
            const headerFooterSpace = (checkboxOptions.withProjectName || checkboxOptions.withTimestamp || checkboxOptions.withPageNumbers) ? marginSize : 0;
            const availableHeightPerPage = previewHeight - (marginSize * 2) - headerFooterSpace;

            // Get content rows and sort them
            const contentRows = Object.entries(projectScanForSend)
                .filter(([key]) => key.startsWith("Row "))
                .sort(([aKey], [bKey]) => {
                    const aNum = parseInt(aKey.replace("Row ", ""));
                    const bNum = parseInt(bKey.replace("Row ", ""));
                    return aNum - bNum;
                });

            // If no content, create single empty page
            if (contentRows.length === 0) {
                setPages([{
                    pageNumber: 1,
                    content: isSpreadsheetFormat ? [] : []
                }]);
                setTotalPages(1);
                return;
            }

            // For spreadsheet format, we'll keep the existing logic but add text breaking within cells
            if (isSpreadsheetFormat) {
                // Create a temporary table to measure actual heights
                const tempTable = document.createElement('table');
                tempTable.style.position = 'absolute';
                tempTable.style.top = '-9999px';
                tempTable.style.left = '-9999px';
                tempTable.style.width = `${previewWidth - (marginSize * 2)}px`;
                tempTable.style.borderCollapse = 'collapse';
                tempTable.style.fontSize = `${12 * scaleFactor}px`;
                tempTable.style.visibility = 'hidden';

                // Add table header
                const thead = document.createElement('thead');
                const headerRow = document.createElement('tr');
                ['Card Data', 'Card Content', 'Tags'].forEach((headerText, index) => {
                    const th = document.createElement('th');
                    th.textContent = headerText;
                    th.style.backgroundColor = '#f0f0f0';
                    th.style.fontWeight = 'bold';
                    th.style.textAlign = 'center';
                    th.style.padding = `${8 * scaleFactor}px`;
                    th.style.border = `${1 * scaleFactor}px solid #000`;
                    th.style.fontSize = `${12 * scaleFactor}px`;
                    th.style.width = index === 1 ? '50%' : '25%';
                    headerRow.appendChild(th);
                });
                thead.appendChild(headerRow);
                tempTable.appendChild(thead);

                document.body.appendChild(tempTable);
                const headerHeight = tempTable.offsetHeight;
                document.body.removeChild(tempTable);

                // Calculate how much space is left for content after header
                const availableContentHeight = availableHeightPerPage - headerHeight;
                const maxRowHeight = availableContentHeight * 0.8; // Don't let single row take entire page

                // Process each row and split if necessary
                const processedRows = [];
                contentRows.forEach(([key, rowData]) => {
                    // Split large content if necessary
                    const contentChunks = splitTableCellContent(preserveParagraphs(rowData.Content), maxRowHeight);
                    const annotationChunks = (checkboxOptions.withAnnotations && rowData["Annotation"])
                        ? splitTableCellContent(preserveParagraphs(rowData["Annotation"]), maxRowHeight)
                        : [];

                    // Create rows for each content chunk
                    contentChunks.forEach((contentChunk, index) => {
                        processedRows.push({
                            key: `${key}-content-${index}`,
                            type: 'main',
                            data: {
                                ...rowData,
                                Content: contentChunk,
                                isContentContinuation: index > 0
                            }
                        });
                    });

                    // Create rows for each annotation chunk
                    annotationChunks.forEach((annotationChunk, index) => {
                        processedRows.push({
                            key: `${key}-annotation-${index}`,
                            type: 'annotation',
                            data: {
                                ...rowData,
                                Annotation: annotationChunk,
                                isAnnotationContinuation: index > 0
                            }
                        });
                    });
                });

                // Now measure and paginate the processed rows
                const measuredRows = [];
                processedRows.forEach(processedRow => {
                    // Create temporary table with this row to measure
                    const tempTableWithRow = document.createElement('table');
                    tempTableWithRow.style.position = 'absolute';
                    tempTableWithRow.style.top = '-9999px';
                    tempTableWithRow.style.left = '-9999px';
                    tempTableWithRow.style.width = `${previewWidth - (marginSize * 2)}px`;
                    tempTableWithRow.style.borderCollapse = 'collapse';
                    tempTableWithRow.style.fontSize = `${12 * scaleFactor}px`;
                    tempTableWithRow.style.visibility = 'hidden';

                    const tbody = document.createElement('tbody');
                    const row = document.createElement('tr');

                    if (processedRow.type === 'main') {
                        // Main row
                        const col1 = document.createElement('td');
                        col1.style.padding = `${8 * scaleFactor}px`;
                        col1.style.border = `${1 * scaleFactor}px solid #000`;
                        col1.style.verticalAlign = 'top';
                        col1.style.width = '25%';
                        col1.style.fontSize = `${12 * scaleFactor}px`;

                        if (!processedRow.data.isContentContinuation) {
                            if (checkboxOptions.withCardTitle && processedRow.data["Card Title"]) {
                                const p1 = document.createElement('p');
                                p1.style.margin = `0 0 ${6 * scaleFactor}px 0`;
                                p1.textContent = processedRow.data["Card Title"];
                                col1.appendChild(p1);
                            }
                            if (checkboxOptions.withCardMetadata && processedRow.data["Card Metadata"]) {
                                const p2 = document.createElement('p');
                                p2.style.margin = `0 0 ${6 * scaleFactor}px 0`;
                                p2.textContent = processedRow.data["Card Metadata"];
                                col1.appendChild(p2);
                            }
                        }

                        const col2 = document.createElement('td');
                        col2.style.padding = `${8 * scaleFactor}px`;
                        col2.style.border = `${1 * scaleFactor}px solid #000`;
                        col2.style.verticalAlign = 'top';
                        col2.style.width = '50%';
                        col2.style.fontSize = `${12 * scaleFactor}px`;
                        col2.innerHTML = processedRow.data.Content;

                        const col3 = document.createElement('td');
                        col3.style.padding = `${8 * scaleFactor}px`;
                        col3.style.border = `${1 * scaleFactor}px solid #000`;
                        col3.style.verticalAlign = 'top';
                        col3.style.width = '25%';
                        col3.style.fontSize = `${12 * scaleFactor}px`;

                        if (!processedRow.data.isContentContinuation && checkboxOptions.withTags && processedRow.data["Tags"]) {
                            const p3 = document.createElement('p');
                            p3.style.margin = '0';
                            p3.textContent = processedRow.data["Tags"];
                            col3.appendChild(p3);
                        }

                        row.appendChild(col1);
                        row.appendChild(col2);
                        row.appendChild(col3);
                    } else {
                        // Annotation row
                        const annotationCol1 = document.createElement('td');
                        annotationCol1.style.padding = `${8 * scaleFactor}px`;
                        annotationCol1.style.border = `${1 * scaleFactor}px solid #000`;
                        annotationCol1.style.verticalAlign = 'top';
                        annotationCol1.style.width = '25%';
                        annotationCol1.style.fontSize = `${12 * scaleFactor}px`;

                        if (!processedRow.data.isAnnotationContinuation) {
                            const annotationLabel = document.createElement('p');
                            annotationLabel.style.margin = '0';
                            annotationLabel.textContent = `Annotation for: ${processedRow.data["Card Metadata"] || "Card"}`;
                            annotationCol1.appendChild(annotationLabel);
                        }

                        const annotationCol2 = document.createElement('td');
                        annotationCol2.style.padding = `${8 * scaleFactor}px`;
                        annotationCol2.style.border = `${1 * scaleFactor}px solid #000`;
                        annotationCol2.style.verticalAlign = 'top';
                        annotationCol2.style.width = '50%';
                        annotationCol2.style.fontSize = `${12 * scaleFactor}px`;
                        annotationCol2.innerHTML = processedRow.data.Annotation;

                        const annotationCol3 = document.createElement('td');
                        annotationCol3.style.padding = `${8 * scaleFactor}px`;
                        annotationCol3.style.border = `${1 * scaleFactor}px solid #000`;
                        annotationCol3.style.verticalAlign = 'top';
                        annotationCol3.style.width = '25%';

                        row.appendChild(annotationCol1);
                        row.appendChild(annotationCol2);
                        row.appendChild(annotationCol3);
                    }

                    tbody.appendChild(row);
                    tempTableWithRow.appendChild(tbody);
                    document.body.appendChild(tempTableWithRow);
                    const rowHeight = tempTableWithRow.offsetHeight;
                    document.body.removeChild(tempTableWithRow);

                    measuredRows.push({
                        ...processedRow,
                        height: rowHeight
                    });
                });

                // Distribute rows across pages based on measured heights
                const pageData = [];
                let currentPageRows = [];
                let currentPageHeight = 0;
                let pageNumber = 1;

                measuredRows.forEach((measuredRow, index) => {
                    // Check if this row fits on current page
                    if (currentPageHeight + measuredRow.height <= availableContentHeight || currentPageRows.length === 0) {
                        currentPageRows.push([measuredRow.key, measuredRow.data, measuredRow.type]);
                        currentPageHeight += measuredRow.height;
                    } else {
                        // Current page is full, create it and start new page
                        pageData.push({
                            pageNumber,
                            content: [...currentPageRows],
                            isSpreadsheet: true
                        });
                        pageNumber++;

                        // Start new page with current row
                        currentPageRows = [[measuredRow.key, measuredRow.data, measuredRow.type]];
                        currentPageHeight = measuredRow.height;
                    }

                    // If this is the last row, add the final page
                    if (index === measuredRows.length - 1) {
                        pageData.push({
                            pageNumber,
                            content: [...currentPageRows],
                            isSpreadsheet: true
                        });
                    }
                });

                // Ensure at least one page exists
                if (pageData.length === 0) {
                    pageData.push({
                        pageNumber: 1,
                        content: [],
                        isSpreadsheet: true
                    });
                }

                setPages(pageData);
                setTotalPages(pageData.length);
                return;
            }

            // Document format with text breaking
            // Create all content elements first
            const allContentElements = [];
            contentRows.forEach(([key, rowData]) => {
                // Add card title if checkbox is checked and title exists
                if (checkboxOptions.withCardTitle && rowData["Card Title"]) {
                    allContentElements.push({
                        content: `<strong>${rowData["Card Title"]}</strong>`,
                        type: 'paragraph',
                        styles: { fontWeight: 'bold', fontFamily: 'sans-serif' }
                    });
                }

                // Add card metadata if checkbox is checked and metadata exists
                if (checkboxOptions.withCardMetadata && rowData["Card Metadata"]) {
                    allContentElements.push({
                        content: rowData["Card Metadata"],
                        type: 'paragraph',
                        styles: { fontFamily: 'sans-serif' }
                    });
                }

                // Add tags if checkbox is checked and tags exist
                if (checkboxOptions.withTags && rowData["Tags"]) {
                    allContentElements.push({
                        content: `<strong>Tags:</strong> ${rowData["Tags"]}`,
                        type: 'paragraph',
                        styles: { fontFamily: 'sans-serif' }
                    });
                }

                // Parse and add main content paragraphs
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = rowData.Content;
                const contentParagraphs = Array.from(tempDiv.children);

                contentParagraphs.forEach(p => {
                    const innerHTML = p.innerHTML || '';
                    if (innerHTML.trim()) {
                        // Get the complete outer HTML to preserve all styling
                        const outerHTML = p.outerHTML;

                        allContentElements.push({
                            content: outerHTML, // Use complete HTML with all styling
                            type: 'paragraph',
                            styles: {
                                lineHeight: p.style.lineHeight || '1.5',
                                fontWeight: p.style.fontWeight || 'normal',
                                fontStyle: p.style.fontStyle || 'normal',
                                textDecoration: p.style.textDecoration || 'none',
                                color: p.style.color || 'inherit',
                                fontFamily: extractFontFamily(p)
                            }
                        });
                    }
                });

                // Add annotation if checkbox is checked and annotation exists
                if (checkboxOptions.withAnnotations && rowData["Annotation"]) {
                    allContentElements.push({
                        content: '<strong>Annotation:</strong>',
                        type: 'paragraph',
                        styles: { fontWeight: 'bold', fontFamily: 'sans-serif' }
                    });

                    const annotationDiv = document.createElement('div');
                    annotationDiv.innerHTML = rowData["Annotation"];
                    const annotationParagraphs = Array.from(annotationDiv.children);

                    annotationParagraphs.forEach(p => {
                        const innerHTML = p.innerHTML || '';
                        if (innerHTML.trim()) {
                            // Get the complete outer HTML to preserve all styling
                            const outerHTML = p.outerHTML;

                            allContentElements.push({
                                content: outerHTML, // Use complete HTML with all styling
                                type: 'paragraph',
                                styles: {
                                    lineHeight: p.style.lineHeight || '1.5',
                                    fontWeight: p.style.fontWeight || 'normal',
                                    fontStyle: p.style.fontStyle || 'normal',
                                    textDecoration: p.style.textDecoration || 'none',
                                    color: p.style.color || 'inherit',
                                    fontFamily: extractFontFamily(p)
                                }
                            });
                        }
                    });
                }
            });

            // Now process each element and split if necessary
            const processedElements = [];
            allContentElements.forEach((element, elementIndex) => {
                const paragraphStyles = {
                    textIndent: `${24 * scaleFactor}px`,
                    marginBottom: `${12 * scaleFactor}px`,
                    fontSize: `${12 * scaleFactor}px`,
                    lineHeight: element.styles.lineHeight || '1.5',
                    fontWeight: element.styles.fontWeight || 'normal',
                    fontFamily: element.styles.fontFamily || 'sans-serif'
                };

                // Check if this paragraph fits in available height
                const tempDiv = document.createElement('div');
                tempDiv.style.position = 'absolute';
                tempDiv.style.top = '-9999px';
                tempDiv.style.left = '-9999px';
                tempDiv.style.width = `${previewWidth - (marginSize * 2)}px`;
                tempDiv.style.visibility = 'hidden';
                Object.assign(tempDiv.style, paragraphStyles);
                tempDiv.innerHTML = element.content; // Use innerHTML to preserve styling

                document.body.appendChild(tempDiv);
                const elementHeight = tempDiv.offsetHeight;
                document.body.removeChild(tempDiv);

                if (elementHeight <= availableHeightPerPage) {
                    // Element fits, add as-is with original HTML content
                    processedElements.push({
                        content: element.content, // Keep original HTML content
                        height: elementHeight,
                        styles: paragraphStyles,
                        originalIndex: elementIndex
                    });
                } else {
                    // Element is too tall, split it while preserving HTML structure
                    const chunks = splitTextContent(element.content, availableHeightPerPage * 0.9, paragraphStyles);

                    chunks.forEach((chunk, chunkIndex) => {
                        // Measure each chunk
                        const chunkDiv = document.createElement('div');
                        chunkDiv.style.position = 'absolute';
                        chunkDiv.style.top = '-9999px';
                        chunkDiv.style.left = '-9999px';
                        chunkDiv.style.width = `${previewWidth - (marginSize * 2)}px`;
                        chunkDiv.style.visibility = 'hidden';
                        Object.assign(chunkDiv.style, paragraphStyles);
                        chunkDiv.innerHTML = chunk; // Use innerHTML for styled chunks

                        document.body.appendChild(chunkDiv);
                        const chunkHeight = chunkDiv.offsetHeight;
                        document.body.removeChild(chunkDiv);

                        processedElements.push({
                            content: chunk, // Preserve HTML content in chunks
                            height: chunkHeight,
                            styles: paragraphStyles,
                            originalIndex: elementIndex,
                            chunkIndex: chunkIndex
                        });
                    });
                }
            });

            // Distribute processed elements across pages
            const pageData = [];
            let currentPageContent = [];
            let currentPageHeight = 0;
            let pageNumber = 1;

            processedElements.forEach((element, index) => {
                // Check if this element fits on current page
                if (currentPageHeight + element.height <= availableHeightPerPage || currentPageContent.length === 0) {
                    currentPageContent.push(element);
                    currentPageHeight += element.height;
                } else {
                    // Current page is full, create it and start new page
                    pageData.push({
                        pageNumber,
                        content: [...currentPageContent],
                        isSpreadsheet: false
                    });
                    pageNumber++;

                    // Start new page with current element
                    currentPageContent = [element];
                    currentPageHeight = element.height;
                }

                // If this is the last element, add the final page
                if (index === processedElements.length - 1) {
                    pageData.push({
                        pageNumber,
                        content: [...currentPageContent],
                        isSpreadsheet: false
                    });
                }
            });

            // Ensure at least one page exists
            if (pageData.length === 0) {
                pageData.push({
                    pageNumber: 1,
                    content: [],
                    isSpreadsheet: false
                });
            }

            setPages(pageData);
            setTotalPages(pageData.length);
        };

        if (Object.keys(projectScanForSend).length > 1) { // More than just "Project Name"
            calculatePages();
        } else {
            setPages([{
                pageNumber: 1,
                content: [],
                isSpreadsheet: isSpreadsheetFormat
            }]);
            setTotalPages(1);
        }
    }, [projectScanForSend, previewHeight, marginSize, scaleFactor, checkboxOptions, previewWidth, isSpreadsheetFormat]);

    return (
        <PrintPreviewFunctions
            pages={pages}
            totalPages={totalPages}
            previewStyle={previewStyle}
            contentStyle={contentStyle}
            checkboxOptions={checkboxOptions}
            projectScanForSend={projectScanForSend}
            headerStyle={headerStyle}
            headerFooterStyle={headerFooterStyle}
            footerStyle={footerStyle}
            tableStyle={tableStyle}
            tableHeaderStyle={tableHeaderStyle}
            tableCellStyle={tableCellStyle}
            scaleFactor={scaleFactor}
            previewHeight={previewHeight}
            marginSize={marginSize}
        />
    );
};

export default PrintPreview;